import { useState } from "react";
import clsx from "clsx";
import Button from "../components/Button";
import Filter from "../components/Filter";
import Count from "../components/Count";
import FilterIcon from "../../src/assets/svg/filter.svg";
import ArrowLeft from "../../src/assets/svg/arrowLeft.svg";
import ColorPicker from "./ColorPicker";
import ButtonComplex from "./ButtonComplex";
import { Slider } from "@mui/material";

import { SetURLSearchParams, URLSearchParamsInit } from "react-router-dom"; // Import the correct type


export interface FilterMenuProps {
  className?: string;
  filterData: Record<string, string[]>;
  onFilterApply: (filters: Record<string, string[]>) => void;
  selectedFilters: Record<string, string[]>;
  handleFilterChange: (name: string, values: string[]) => void;
  resetFilters: () => void;
  filtersCount: number;
  setSearchParams: (params: URLSearchParamsInit) => void; // Correct type for setSearchParams
}


export function FilterMenu({
  className,
  filterData,
  onFilterApply,
  selectedFilters,
  handleFilterChange,
  resetFilters,
  filtersCount,
  setSearchParams,
}: FilterMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const [tempPriceRange, setTempPriceRange] = useState<string[]>(["0", "60000"]); // Temporary state for inputs
  const [priceRange, setPriceRange] = useState<number[]>([0, 60000]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, type: "min" | "max") => {
    const value = e.target.value;

    if (type === "min") {
      setTempPriceRange([value, tempPriceRange[1]]);
    } else if (type === "max") {
      setTempPriceRange([tempPriceRange[0], value]);
    }
  };

  const handleInputBlur = (type: "min" | "max") => {
    let [min, max] = tempPriceRange.map((val) => parseInt(val, 10) || 0);

    min = Math.max(0, Math.min(min, max)); 
    max = Math.min(60000, Math.max(max, min)); 

    setPriceRange([min, max]);
    setTempPriceRange([min.toString(), max.toString()]); 
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setPriceRange(newValue as number[]);
    setTempPriceRange((newValue as number[]).map(String)); 
  };

  const toggleDrawer = () => setIsOpen(!isOpen);

  const handleResetFilters = () => {
    setSearchParams({});
    setTempPriceRange(["0", "60000"]); 
    setPriceRange([0, 60000]); 

    resetFilters();
  };
  
  

  const applyFilters = () => {
    const updatedFilters = {
      ...selectedFilters,
      rasponcijene: [String(priceRange[0]), String(priceRange[1])],
    };
  
    // Convert filters into query parameters
    const queryParams = Object.entries(updatedFilters).reduce((acc, [key, values]) => {
      acc[key] = values.join(",");
      return acc;
    }, {} as Record<string, string>);
  
    // Use setSearchParams
    setSearchParams(queryParams);
  
    onFilterApply(updatedFilters); // Notify the parent
    toggleDrawer();
  };
  

  return (
    <>
      {/* Filter icon button */}
      <ButtonComplex
        label="Filtrirajte"
        className={className}
        icon={
          <img src={FilterIcon} alt="Filter" className="w-3.5 h-[12.77px]" />
        }
        onClick={toggleDrawer}
      >
        {filtersCount > 0 && <Count size="sm" count={filtersCount}></Count>}
      </ButtonComplex>

      {/* Overlay background */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 cursor-pointer lg:hidden bg-opacity-35 bg-gray-extra-dark"
          onClick={toggleDrawer}
        />
      )}

      {/* Drawer */}
      {isOpen && (
        <div
          className={clsx(
            "fixed top-0 right-0 w-[90%] sm:w-[524px] lg:hidden h-full bg-gray-extra-light border-l-2 border-gray-light z-50 transform transition-transform duration-300 overflow-scroll",
            isOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          <div className="flex flex-col gap-6 px-6 py-6">
            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <img
                  src={ArrowLeft}
                  alt="Close"
                  onClick={toggleDrawer}
                  className="cursor-pointer"
                ></img>
                <h2 className="text-sm md:text-lg text-gray-extra-dark">
                  Filtrirajte
                </h2>
              </div>
              <div className="flex flex-col gap-3">
                {Object.entries(filterData)
                  .filter(([filterName]) => filterName !== "Boja")
                  .map(([filterName, options]) => (
                    <Filter
                      key={filterName}
                      name={filterName}
                      data={options}
                      selected={selectedFilters[filterName] || []} // Pass current selections
                      onChange={(values) =>
                        handleFilterChange(filterName, values)
                      } // Handle multiselect changes
                    />
                  ))}
              </div>
              <hr className="w-full border-t-1 border-extra-light" />

              {/* Slider and Inputs for Price Range */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div>
                    <label
                      htmlFor="minPrice"
                      className="mb-1 text-xs text-gray-400"
                    >
                      Min. cijena
                    </label>
                    <input
                      id="minPrice"
                      type="number"
                      className="inline-flex justify-between w-full p-3 text-sm border rounded appearance-none focus:border-primary-blue focus:outline-none text-gray-extra-dark"
                      value={tempPriceRange[0]} // Bind to temporary state
                      onChange={(e) => handleInputChange(e, "min")}
                      onBlur={() => handleInputBlur("min")} // Update state on blur
                      min={0}
                      max={priceRange[1]}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="maxPrice"
                      className="mb-1 text-xs text-gray-400"
                    >
                      Max. cijena
                    </label>
                    <input
                      id="maxPrice"
                      type="number"
                      className="inline-flex justify-between w-full p-3 text-sm border rounded appearance-none focus:border-primary-blue focus:outline-none text-gray-extra-dark"
                      value={tempPriceRange[1]} // Bind to temporary state
                      onChange={(e) => handleInputChange(e, "max")}
                      onBlur={() => handleInputBlur("max")} // Update state on blur
                      min={priceRange[0]}
                      max={60000}
                    />
                  </div>
                </div>

                {/* Slider */}
                <Slider
                  value={priceRange}
                  onChange={handleSliderChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={60000}
                  step={1000}
                  marks={[
                    { value: 0, label: "0" },
                    { value: 30000, label: "30.000" },
                    { value: 60000, label: "60.000" },
                  ]}
                />
              </div>
              <hr className="w-full border-t-1 border-extra-light" />
              <ColorPicker
                colors={filterData.Boja}
                selected={selectedFilters.Boja || []}
                onChange={(values) => handleFilterChange("Boja", values)}
              />
            </div>
            <hr className="w-full border-t-1 border-extra-light" />
            <div className="flex flex-col gap-2">
              <Button
                text="Filtrirajte"
                variant="primary"
                size="base"
                width="w-full"
                action={applyFilters}
              />
              <Button
                text="Očistite filter"
                variant="secondary"
                size="base"
                width="w-full"
                action={handleResetFilters}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FilterMenu;
